import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Quote, CheckCircle2, Star } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { AnimatedCounter } from "../components/AnimatedCounter";
import { CTABanner } from "../components/CTABanner";
import { getIcon } from "../data/site";
import { useSite } from "../context/ContentContext";

export default function Home() {
  const { hero, stats, services, whyChoose, industries, testimonials } = useSite();

  return (
    <div data-testid="home-page">
      <Seo
        title="Global Trade, Technology, Agriculture & Logistics"
        description="Domaleo Enterprise delivers world-class business solutions through import & export, IT services, logistics, agricultural solutions, and general supply in Accra, Ghana."
      />

      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={hero.image} alt="Global trade cargo ship at port" className="h-full w-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative container-px mx-auto max-w-7xl pt-28 pb-16">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-white backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-accent" /> {hero.badge}
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-white text-balance">{hero.title}</h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-200">{hero.subtitle}</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-accent px-8 py-4 text-sm font-semibold text-[#06281A] shadow-xl transition-transform duration-300 hover:scale-105" data-testid="hero-cta-quote">
                Request a Quote <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/services" className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/5 px-8 py-4 text-sm font-semibold text-white backdrop-blur transition-colors hover:bg-white/15" data-testid="hero-cta-services">
                Explore Our Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="relative z-10 -mt-16 container-px mx-auto max-w-7xl">
        <div className="grid grid-cols-2 gap-4 rounded-3xl border border-border bg-card p-6 shadow-xl sm:p-10 lg:grid-cols-4">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.1} className="text-center">
              <div className="font-heading text-4xl lg:text-5xl font-bold text-primary">
                <AnimatedCounter value={Number(s.value)} suffix={s.suffix} />
              </div>
              <p className="mt-2 text-sm font-medium text-muted-foreground">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FEATURED SERVICES */}
      <section className="container-px mx-auto max-w-7xl py-20 lg:py-28">
        <SectionHeading overline="Our Services" title="Comprehensive solutions across five sectors" subtitle="From global sourcing to digital transformation, we cover the full spectrum of your business needs." />
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {services.map((s, i) => {
            const Icon = getIcon(s.iconName);
            const featured = i === 0;
            return (
              <Reveal key={s.slug} delay={(i % 3) * 0.08} className={featured ? "lg:col-span-2" : ""}>
                <Link to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`} className="group relative flex h-full min-h-[260px] flex-col justify-end overflow-hidden rounded-2xl border border-border">
                  <img src={s.image} alt={s.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#06281A] via-[#06281A]/70 to-transparent" />
                  <div className="relative p-6">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 text-white backdrop-blur"><Icon className="h-5 w-5" strokeWidth={1.6} /></span>
                    <h3 className="mt-4 font-heading text-xl font-semibold text-white">{s.title}</h3>
                    <p className="mt-2 text-sm text-slate-300 line-clamp-2">{s.short}</p>
                    <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-accent">Learn more <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="bg-surface py-20 lg:py-28">
        <div className="container-px mx-auto max-w-7xl">
          <SectionHeading overline="Why Domaleo" title="Why clients choose us" subtitle="A dependable, customer-focused partner built on experience and operational excellence." />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {whyChoose.map((w, i) => {
              const Icon = getIcon(w.iconName);
              return (
                <Reveal key={w.title} delay={(i % 3) * 0.08}>
                  <div className="group h-full rounded-2xl border border-border bg-card p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-white"><Icon className="h-6 w-6" strokeWidth={1.6} /></span>
                    <h3 className="mt-5 font-heading text-lg font-semibold">{w.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{w.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* INDUSTRIES marquee */}
      <section className="py-20 lg:py-24 overflow-hidden">
        <div className="container-px mx-auto max-w-7xl">
          <SectionHeading overline="Industries Served" title="Trusted across sectors" align="center" />
        </div>
        <div className="relative mt-12 flex overflow-hidden">
          <div className="flex shrink-0 animate-marquee gap-4 pr-4">
            {[...industries, ...industries].map((ind, i) => (
              <span key={i} className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-border bg-card px-6 py-3 text-sm font-medium"><CheckCircle2 className="h-4 w-4 text-secondary" /> {ind}</span>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-surface py-20 lg:py-28">
        <div className="container-px mx-auto max-w-7xl">
          <SectionHeading overline="Testimonials" title="What our clients say" subtitle="Real results from businesses and institutions we've served." />
          <div className="mt-12 grid gap-5 md:grid-cols-2">
            {testimonials.map((t, i) => (
              <Reveal key={t.name} delay={(i % 2) * 0.1}>
                <figure className="h-full rounded-2xl border border-border bg-card p-8">
                  <Quote className="h-8 w-8 text-accent" />
                  <div className="mt-3 flex gap-1">{Array.from({ length: 5 }).map((_, k) => <Star key={k} className="h-4 w-4 fill-accent text-accent" />)}</div>
                  <blockquote className="mt-4 text-base leading-relaxed text-foreground/90">"{t.text}"</blockquote>
                  <figcaption className="mt-5"><p className="font-heading font-semibold">{t.name}</p><p className="text-sm text-muted-foreground">{t.role}</p></figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABanner />
    </div>
  );
}
